import React, { useState, useEffect, useMemo } from 'react';
import CenteredModal from '../CenteredModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faExclamationTriangle, faPaperPlane, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import RolePermissionAPI from '~/api/rolePermissionApi';

function RequestPermissionModal({ show, onClose, user }) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [allPermissions, setAllPermissions] = useState([]);
    const [userPermissions, setUserPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [reason, setReason] = useState('');
    const [openGroups, setOpenGroups] = useState({});

    const toggleGroup = (group) => {
        setOpenGroups(prev => ({
            ...prev,
            [group]: prev[group] === false ? true : false
        }));
    };

    useEffect(() => {
        if (show && user) {
            fetchData();
            setSelectedPermissions([]);
            setReason('');
            setSuccessMsg('');
            setError('');
        } else {
            setAllPermissions([]);
            setUserPermissions([]);
            setError('');
            setSuccessMsg('');
        }
    }, [show, user]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');
            const [permsRes, userRes] = await Promise.all([
                RolePermissionAPI.getAllPermissions(),
                RolePermissionAPI.getUserPermissions(user.id)
            ]);

            if (permsRes.data?.Succeeded) {
                setAllPermissions(permsRes.data.Data || []);
            }
            if (userRes.data?.Succeeded) {
                setUserPermissions(userRes.data.Data || []);
            } else {
                setError(userRes.data?.Message || 'Failed to load user permissions.');
            }
        } catch (err) {
            setError(err.response?.data?.Message || 'Failed to load permissions.');
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePermission = (permissionId) => {
        setSelectedPermissions(prev => {
            if (prev.includes(permissionId)) {
                let newPerms = prev.filter(p => p !== permissionId);
                // If removing an '.Access' permission, also remove dependent actions
                if (permissionId.endsWith('.Access')) {
                    const modulePrefix = permissionId.replace('.Access', '.');
                    newPerms = newPerms.filter(p => !p.startsWith(modulePrefix));
                }
                return newPerms;
            } else {
                let newPerms = [...prev, permissionId];
                // If adding an action, auto-select '.Access'
                if (!permissionId.endsWith('.Access') && permissionId.includes('.')) {
                    const modulePrefix = permissionId.substring(0, permissionId.lastIndexOf('.'));
                    const accessPermId = `${modulePrefix}.Access`;

                    if (!prev.includes(accessPermId) && !userPermissions.includes(accessPermId) && allPermissions.includes(accessPermId)) {
                        newPerms.push(accessPermId);
                    }
                }
                return newPerms;
            }
        });
    };

    const handleToggleGroup = (groupPerms, isChecked) => {
        const permIds = groupPerms.map(p => p.id);
        setSelectedPermissions(prev => {
            if (isChecked) {
                const newPerms = permIds.filter(id => !prev.includes(id) && !userPermissions.includes(id));
                return [...prev, ...newPerms];
            } else {
                return prev.filter(p => !permIds.includes(p));
            }
        });
    };

    const handleSubmit = async () => {
        if (selectedPermissions.length === 0) {
            setError('Please select at least one permission to request.');
            return;
        }
        if (reason.trim() === '') {
            setError('Please provide a reason for requesting these permissions.');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            setSuccessMsg('');
            const data = selectedPermissions.map(p => ({
                RequestedPermission: p,
                Reason: reason
            }));
            const res = await RolePermissionAPI.createPermissionRequestsBulk(data);
            if (res.data?.Succeeded) {
                setSuccessMsg(res.data.Message || 'Request submitted successfully.');
                setTimeout(() => {
                    onClose();
                }, 5000);
            } else {
                setError(res.data?.Message || 'Failed to submit request.');
            }
        } catch (err) {
            setError(err.response?.data?.Message || 'Failed to submit request.');
        } finally {
            setSubmitting(false);
        }
    };

    const missingPermissions = useMemo(() => {
        return allPermissions.filter(p => !userPermissions.includes(p) && !p.includes('Header.CreateAdminAccount') && !p.includes('Header.RoleManagement') && !p.includes('SystemSettings'));
    }, [allPermissions, userPermissions]);

    const groupedPermissions = useMemo(() => {
        const getGroupPath = (rawModule) => {
            const map = {
                'DashboardAnalytics': ['Dashboard', 'Analytics'],
                'DashboardMetrics': ['Dashboard', 'Metrics'],
                'UserRole': ['User Management', 'User Role'],
                'FileManager': ['File Management', 'Image Gallery'],
                'AssetsImage': ['File Management', 'Assets Image'],
                'FileImageUrl': ['File Management', 'Image Url'],
                'PaymentInformation': ['Payment & Subscription', 'Payment Information'],
                'StripePayments': ['Payment & Subscription', 'Stripe Payments'],
                'LandingPageAboutUs': ['Landing Page', 'About Us'],
                'LandingPageOurService': ['Landing Page', 'Our Service'],
                'LandingPagePrivateBlogPost': ['Landing Page', 'Private Blog Post'],
                'LandingPageRoute': ['Landing Page', 'Route'],
                'LandingPageArticle': ['Landing Page', 'Article'],
                'LandingPageTerms': ['Landing Page', 'Terms'],
                'LandingPageHome': ['Landing Page', 'Home'],
                'Appointment': ['System Management', 'General', 'Appointment'],
                'ApplicationParam': ['System Management', 'General', 'Application Parameter'],
                'SupportTicket': ['AI Chatbot Page', 'Support Ticket History'],
                'Notification': ['System Management', 'General', 'Notification'],
                'SystemJob': ['System Management', 'General', 'System Jobs'],
                'DatabaseManagement': ['System Management', 'Database', 'Database Management'],
                'EmailTemplate': ['System Management', 'Email & Messaging', 'Email Template'],
                'SmtpSettings': ['System Management', 'Email & Messaging', 'SMTP Settings'],
                'Mailjet': ['System Management', 'Email & Messaging', 'Mailjet'],
                'NotiInAppTemplate': ['System Management', 'Email & Messaging', 'In-App Notification Template'],
                'AuditLog': ['System Management', 'Logs Management', 'Audit Log'],
                'SystemException': ['System Management', 'Logs Management', 'System Exception'],
                'UserVisitor': ['User Management', 'User Visitor'],
                'UserInformation': ['User Management', 'User Information'],
                'UserPlanSubscription': ['Payment & Subscription', 'User Plan Subscription'],
                'ServiceManager': ['Payment & Subscription', 'Service Manager'],
                'UserCardManagement': ['Payment & Subscription', 'User Card Management'],
                'ChatbotManagement': ['AI Chatbot Page', 'Chatbot Management'],
                'ChatbotFeedback': ['AI Chatbot Page', 'Chat Feedback'],
                'AwsChatbot': ['AI Chatbot Page', 'Manage Chat Sessions'],
                'QdrantSync': ['AI Chatbot Page', 'Qdrant Vector Sync'],
                'AiServicesUsage': ['System Management', 'AI Services', 'Usage'],
                'AiServicesPricing': ['System Management', 'AI Services', 'OpenAI Pricing'],
                'AiServicesMasterData': ['System Management', 'AI Services', 'Master Data'],
                'AiServicesTrafficAnalytics': ['System Management', 'AI Services', 'Traffic Analytics'],
                'ApiMonitoring': ['System Management', 'API Monitoring & Testing'],
                'HeaderDatabaseManagement': ['Header', 'Database Management'],
                'HeaderSystemServicesTesting': ['Header', 'System Services Testing'],
                'HeaderSecuritySettings': ['Header', 'Security Settings'],
                'HeaderSearchSettings': ['Header', 'Search Settings'],
                'HeaderCreateUserAccount': ['Header', 'Create User Account'],
                'SecuritySettings': ['Header', 'Security Settings']
            };
            if (map[rawModule]) return map[rawModule];

            if (rawModule.startsWith('LandingPage')) return ['Landing Page', rawModule.replace('LandingPage', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Dashboard')) return ['Dashboard', rawModule.replace('Dashboard', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Aws')) return ['AWS', rawModule.replace('Aws', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Chatbot')) return ['Chatbot', rawModule.replace('Chatbot', '').replace(/([a-z])([A-Z])/g, '$1 $2')];

            return ['Other', rawModule.replace(/([a-z])([A-Z])/g, '$1 $2')];
        };

        const root = {};
        missingPermissions.forEach(p => {
            const parts = p.split('.');
            let rawModule = parts.length > 1 ? parts[1] : 'Other';
            if (rawModule === 'Header' && parts.length > 2) {
                rawModule = 'Header' + parts[2];
            }

            const path = getGroupPath(rawModule);
            const leafNodeName = path[path.length - 1];

            let displayAction = '';
            if (parts.length > 2) {
                let rawAction = parts[2].replace(/([a-z])([A-Z])/g, '$1 $2');
                if (rawModule === 'FileManager' && rawAction === 'Upload') rawAction = 'Upload & Rename';
                if (rawModule === 'FileManager' && rawAction === 'Delete') rawAction = 'Delete & Bulk Delete';
                if (rawModule === 'UserPlanSubscription' && rawAction === 'Assign Plan') rawAction = 'Assign Plan & Assign Specific User';
                if (['LandingPageArticle', 'LandingPageFeatureApp', 'LandingPagePrivateBlogPost'].includes(rawModule) && rawAction === 'Create') rawAction = 'Add New';
                if (['LandingPageAboutUs', 'LandingPageHome', 'LandingPageTerms'].includes(rawModule) && rawAction === 'Create') rawAction = 'Add New & Edit';
                if (rawModule === 'Appointment' && rawAction === 'Create') rawAction = 'Add New';
                if (rawModule === 'SystemJob') {
                    if (rawAction === 'View') rawAction = 'View Detail';
                    if (rawAction === 'Edit') rawAction = 'Enable/Disable';
                }
                if (rawModule === 'SmtpSettings' && rawAction === 'Edit') rawAction = 'Save Configuration & Test Connection';
                if (rawModule === 'AiServicesMasterData' && rawAction === 'Create') rawAction = 'Add New & Edit';
                if (rawModule === 'AuditLog' && rawAction === 'Delete') rawAction = 'Delete (Filter Data & Selected)';
                if (rawModule === 'SystemException' && rawAction === 'Delete') rawAction = 'Delete Selected';
                if (rawModule === 'ChatbotManagement' && rawAction === 'Create') rawAction = 'Add New';
                if (rawModule === 'QdrantSync' && rawAction === 'Compare') rawAction = 'Compare Data';
                displayAction = `${rawAction} ${leafNodeName}`;
            } else {
                displayAction = `Manage ${leafNodeName}`;
            }

            let current = root;
            for (let i = 0; i < path.length; i++) {
                const nodeName = path[i];
                if (i === path.length - 1) {
                    if (!current[nodeName]) current[nodeName] = { _isTab: true, perms: [] };
                    current[nodeName].perms.push({ id: p, action: displayAction });
                } else {
                    if (!current[nodeName]) current[nodeName] = {};
                    current = current[nodeName];
                }
            }
        });

        return root;
    }, [missingPermissions]);

    const renderNode = (nodeName, nodeContent, pathPath, level = 0) => {
        if (nodeContent._isTab) {
            const perms = nodeContent.perms;
            const accessPerm = perms.find(p => p.id.endsWith('.Access'));
            const actionPerms = perms.filter(p => !p.id.endsWith('.Access'));

            const allChecked = perms.every(p => selectedPermissions.includes(p.id));
            const someChecked = perms.some(p => selectedPermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;
            const isOpen = openGroups[pathPath] !== false;

            return (
                <div className="tree-group mb-2" key={pathPath} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <div className="d-flex align-items-center" style={{ padding: '4px 0' }}>
                        <div
                            onClick={() => toggleGroup(pathPath)}
                            style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                        >
                            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-muted" style={{ fontSize: '0.85rem' }} />
                        </div>
                        <div className="form-check m-0 ms-1 d-flex align-items-center">
                            <input
                                className="form-check-input mt-0 me-2 shadow-sm"
                                type="checkbox"
                                id={`group-${pathPath}`}
                                checked={allChecked}
                                ref={input => {
                                    if (input) input.indeterminate = isIndeterminate;
                                }}
                                onChange={(e) => handleToggleGroup(perms, e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            <label
                                className="form-check-label fw-bold"
                                htmlFor={`group-${pathPath}`}
                                style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem', color: '#2f5aa2' }}
                            >
                                {nodeName}
                            </label>
                        </div>
                    </div>

                    {isOpen && (
                        <div className="tree-children ms-4 mt-2 mb-3">
                            {accessPerm && (
                                <div className="form-check mb-2 pb-2" style={{ borderBottom: '1px dashed #e8eaed' }}>
                                    <input
                                        className="form-check-input shadow-sm mt-0"
                                        type="checkbox"
                                        id={accessPerm.id}
                                        checked={selectedPermissions.includes(accessPerm.id)}
                                        onChange={() => handleTogglePermission(accessPerm.id)}
                                        style={{ cursor: 'pointer', transform: 'translateY(2px)' }}
                                    />
                                    <label
                                        className="form-check-label fs-9 ms-2 fw-bold text-primary"
                                        htmlFor={accessPerm.id}
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                    >
                                        Enable Screen Access (Show in Menu{nodeName === 'Image Gallery' ? ', Download, Copy Link' : ''})
                                    </label>
                                </div>
                            )}

                            {actionPerms.length > 0 && (
                                <div className="d-flex flex-column gap-1 ms-2">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>AVAILABLE ACTIONS</div>
                                    {actionPerms.map(p => {
                                        return (
                                            <div className="form-check m-0" key={p.id}>
                                                <input
                                                    className="form-check-input shadow-sm mt-0"
                                                    type="checkbox"
                                                    id={p.id}
                                                    checked={selectedPermissions.includes(p.id)}
                                                    onChange={() => handleTogglePermission(p.id)}
                                                    style={{ cursor: 'pointer', transform: 'translateY(2px)' }}
                                                />
                                                <label
                                                    className="form-check-label fs-9 ms-2 text-body-emphasis"
                                                    htmlFor={p.id}
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    {p.action}
                                                </label>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        } else {
            const isOpen = openGroups[pathPath] !== false;
            const gatherPerms = (obj) => {
                let pList = [];
                Object.values(obj).forEach(val => {
                    if (val._isTab) pList.push(...val.perms);
                    else pList.push(...gatherPerms(val));
                });
                return pList;
            };
            const allNestedPerms = gatherPerms(nodeContent);
            if (allNestedPerms.length === 0) return null;

            const allChecked = allNestedPerms.every(p => selectedPermissions.includes(p.id));
            const someChecked = allNestedPerms.some(p => selectedPermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;

            return (
                <div className="tree-folder mb-3" key={pathPath} style={level === 0 ? { breakInside: 'avoid', pageBreakInside: 'avoid' } : {}}>
                    <div className="d-flex align-items-center" style={{ padding: '6px 0', backgroundColor: level === 0 ? '#fcfcfc' : 'transparent', borderRadius: level === 0 ? '4px' : '0' }}>
                        <div onClick={() => toggleGroup(pathPath)} style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-dark" style={{ fontSize: '0.9rem' }} />
                        </div>
                        <div className="form-check m-0 ms-1 d-flex align-items-center">
                            <input
                                className="form-check-input mt-0 me-2 shadow-sm"
                                type="checkbox"
                                id={`folder-${pathPath}`}
                                checked={allChecked}
                                ref={input => {
                                    if (input) input.indeterminate = isIndeterminate;
                                }}
                                onChange={(e) => handleToggleGroup(allNestedPerms, e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            <label className="form-check-label fw-bold text-dark" htmlFor={`folder-${pathPath}`} style={{ fontSize: level === 0 ? '1rem' : '0.95rem', cursor: 'pointer', userSelect: 'none' }}>
                                {nodeName}
                            </label>
                        </div>
                    </div>

                    {isOpen && (
                        <div className="tree-children ms-4 mt-2">
                            {Object.entries(nodeContent).map(([childName, childContent]) => {
                                if (childName !== '_isTab' && childName !== 'perms') {
                                    return renderNode(childName, childContent, `${pathPath}-${childName}`, level + 1);
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
            );
        }
    };

    return (
        <CenteredModal
            show={show}
            onClose={onClose}
            title="Request Permissions"
            size="xl"
            modalType="modal-xl"
            onConfirm={handleSubmit}
            confirmText="Submit Request"
            closeText="Cancel"
            isSubmitting={submitting}
            disabledConfirmBtn={selectedPermissions.length === 0 || reason.trim() === ''}
        >
            <div className="pb-0 custom-scrollbar" style={{ minHeight: '500px', maxHeight: '75vh', overflowY: 'auto' }}>
                {error && (
                    <div className="auth-error-message mx-3 mt-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <span>{error}</span>
                    </div>
                )}
                {successMsg && (
                    <div className="auth-success-message mx-3 mt-3" style={{ borderRadius: '8px', border: '1px solid #dcfce7' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                        <span style={{ color: '#166534', fontWeight: 600, fontSize: '0.9rem' }}>{successMsg}</span>
                    </div>
                )}

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: '20px 24px' }}>
                        <div className="mb-4">
                            <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Reason for Request <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control shadow-sm"
                                rows="2"
                                placeholder="Explain why you need these permissions..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            ></textarea>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                            <h6 className="fw-bold m-0">Available Permissions</h6>
                            <span className="badge bg-primary rounded-pill">{selectedPermissions.length} selected</span>
                        </div>
                        {(() => {
                            const rootKeys = Object.keys(groupedPermissions).filter(k => k !== '_isTab' && k !== 'perms');

                            if (rootKeys.length === 0) {
                                return (
                                    <div className="text-center text-muted p-4">
                                        You already possess all available permissions.
                                    </div>
                                );
                            }

                            const half = Math.ceil(rootKeys.length / 2);
                            const leftKeys = rootKeys.slice(0, half);
                            const rightKeys = rootKeys.slice(half);

                            return (
                                <div className="row m-0 tree-view-container">
                                    <div className="col-md-6 border-end pe-4">
                                        {leftKeys.map(k => renderNode(k, groupedPermissions[k], k, 0))}
                                    </div>
                                    <div className="col-md-6 ps-4">
                                        {rightKeys.map(k => renderNode(k, groupedPermissions[k], k, 0))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        </CenteredModal>
    );
}

export default RequestPermissionModal;
